import PropTypes from "prop-types";
import "rc-slider/assets/index.css";
import Slider from "rc-slider";
import React from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useState, useEffect } from "react";

function InvestmentLimitsStep({
    formData,
    updateFormData,
    prevStep,
    handleFinalSubmit,
    isSubmitting,
}) {
    const [errors, setErrors] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);
    const [showAccreditedTooltip, setShowAccreditedTooltip] = useState(false);
    const [showNetWorthTooltip, setShowNetWorthTooltip] = useState(false);
    const [annualLimit, setAnnualLimit] = useState(0);
    const [remainingLimit, setRemainingLimit] = useState(0);
    const [showSsn, setShowSsn] = useState(false);

    // Inject custom slider styles
    useEffect(() => {
        const styleId = "slider-styles-investment";
        if(!document.getElementById(styleId)) {
            const style = document.createElement("style");
            style.id = styleId;
            style.textContent = `
        .rc-slider {
          margin: 16px 0;
          padding-bottom: 30px;
        }

        .rc-slider-track {
          background-color: var(--navy);
          height: 4px;
        }

        .rc-slider-rail {
          background-color: #e5e7eb;
          height: 4px;
        }

        .rc-slider-handle {
          width: 16px;
          height: 16px;
          margin-top: -6px;
          background-color: var(--navy);
          border: none;
          box-shadow: 0 2px 4px rgb(var(--navy-rgb) / 0.2);
          opacity: 1;
        }

        .rc-slider-handle:hover {
          box-shadow: 0 2px 8px rgb(var(--navy-rgb) / 0.3);
        }

        .rc-slider-handle-dragging {
          box-shadow: 0 2px 8px rgb(var(--navy-rgb) / 0.3);
        }

        .rc-slider-mark {
          font-size: 12px;
          color: #6b7280;
        }

        .rc-slider-mark-text {
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        .rc-slider-step {
          background: transparent;
        }
      `;
            document.head.appendChild(style);
        }
    }, []);

    // Calculate annual investment limit based on net worth and income
    useEffect(() => {
        const netWorth = parseInt(formData.netWorth) || 0;
        const yearlyIncome = parseInt(formData.yearlyIncome) || 0;

        if(formData.isAccreditedInvestor === "true") {
            // Accredited investors have no federal limits
            setAnnualLimit(0);
        } else {
            // Calculate based on federal regulation crowdfunding limits
            const greater = Math.max(yearlyIncome, netWorth);
            if(netWorth < 124000 || yearlyIncome < 124000) {
                // If either income or net worth is under $124K, invest up to 5% of the greater number
                const fivePercent = greater * 0.05;
                setAnnualLimit(Math.max(2500, fivePercent));
            } else {
                // If both are $124K or more, invest up to 10% of the greater number, max $124,000
                const tenPercent = greater * 0.1;
                setAnnualLimit(Math.min(124000, tenPercent));
            }
        }
    }, [
        formData.netWorth,
        formData.yearlyIncome,
        formData.isAccreditedInvestor,
    ]);

    // Recompute remaining limit when prior invested amount or annual limit changes
    useEffect(() => {
        if(formData.isAccreditedInvestor === "true") {
            setRemainingLimit(0);
            return;
        }
        const prior = parseFloat(formData.priorRegCfAmount || 0) || 0;
        const remaining = Math.max(annualLimit - prior, 0);
        setRemainingLimit(remaining);
    }, [
        formData.netWorth,
        formData.yearlyIncome,
        formData.isAccreditedInvestor,
        formData.priorRegCfAmount,
        annualLimit,
    ]);

    const validateFields = () => {
        const newErrors = {};

        // Validate birthdate (moved here from previous step)
        if(!formData.birthdate) {
            newErrors.birthdate = "Birth date is required";
        } else {
            // Parse as local date parts to avoid UTC shift
            const parts = String(formData.birthdate).split("-").map(Number);
            if(parts.length !== 3) {
                newErrors.birthdate = "Invalid birth date";
            } else {
                const [y, m, d] = parts;
                const dob = new Date(y, m - 1, d);
                const today = new Date();
                const age = today.getFullYear() - dob.getFullYear() -
                    (today < new Date(today.getFullYear(), dob.getMonth(), dob.getDate()) ? 1 : 0);
                if(dob >= today) {
                    newErrors.birthdate = "Birth date must be in the past";
                } else if(age < 18) {
                    newErrors.birthdate = "You must be at least 18 years old to register";
                }
            }
        }

        // Validate SSN
        if(!formData.ssn || !String(formData.ssn).trim()) {
            newErrors.ssn = "SSN is required";
        } else {
            const digits = String(formData.ssn).replace(/\D/g, "");
            if(digits.length !== 9) newErrors.ssn = "SSN must be 9 digits";
        }

        if(!formData.netWorth || formData.netWorth === "") {
            newErrors.netWorth = "Net worth is required";
        }

        if(!formData.yearlyIncome || formData.yearlyIncome === "") {
            newErrors.yearlyIncome = "Annual income is required";
        }

        if(
            !formData.isAccreditedInvestor ||
            formData.isAccreditedInvestor === ""
        ) {
            newErrors.isAccreditedInvestor =
                "Please select whether you are an accredited investor";
        }

        // If they selected "Yes" to accredited investor, require type
        if(
            formData.isAccreditedInvestor === "true" &&
            !formData.accreditedInvestorType
        ) {
            newErrors.accreditedInvestorType =
                "Please select your accredited investor type";
        }

        // Validate prior Reg CF amount (mandatory for all)
        const priorVal = formData.priorRegCfAmount;
        if(priorVal === undefined || priorVal === "") {
            newErrors.priorRegCfAmount =
                "Prior 12-month Reg CF amount is required";
        } else {
            const num = parseFloat(priorVal);
            if(isNaN(num) || num < 0) {
                newErrors.priorRegCfAmount =
                    "Enter a valid non-negative number";
            } else if(
                formData.isAccreditedInvestor !== "true" &&
                annualLimit > 0 &&
                num > annualLimit
            ) {
                newErrors.priorRegCfAmount =
                    "Amount exceeds your annual allowable limit";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if(validateFields()) {
            handleFinalSubmit();
        }
    };

    const handleInputChange = (field, value) => {
        updateFormData({ [field]: value });

        // Clear error when user makes a selection
        if(errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatSsn = (value) => {
        const digits = String(value || "").replace(/\D/g, "").slice(0, 9);

        if(digits.length <= 3) return digits;
        if(digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    };

    const maskSsn = (value) => {
        const digits = String(value || "").replace(/\D/g, "").slice(0, 9);

        if(digits.length <= 4) {
            return digits;
        }

        const maskedPrefix = "x".repeat(Math.max(digits.length - 4, 0));
        const combined = `${maskedPrefix}${digits.slice(-4)}`;

        if(combined.length <= 3) return combined;
        if(combined.length <= 5) return `${combined.slice(0, 3)}-${combined.slice(3)}`;
        return `${combined.slice(0, 3)}-${combined.slice(3, 5)}-${combined.slice(5)}`;
    };

    const ssnDigits = String(formData.ssn || "").replace(/\D/g, "").slice(0, 9);
    const ssnDisplayValue = showSsn ? formatSsn(ssnDigits) : maskSsn(ssnDigits);

    const handleSsnChange = (value) => {
        handleInputChange("ssn", formatSsn(value));
    };

    const handleMaskedSsnKeyDown = (e) => {
        if(showSsn) {
            return;
        }

        const { key, ctrlKey, metaKey } = e;
        const navigationKeys = [
            "Tab",
            "Shift",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            "Home",
            "End",
        ];

        if(ctrlKey || metaKey || navigationKeys.includes(key)) {
            return;
        }

        if(key === "Backspace" || key === "Delete") {
            e.preventDefault();
            handleSsnChange(ssnDigits.slice(0, -1));
            return;
        }

        if(/^\d$/.test(key)) {
            e.preventDefault();
            if(ssnDigits.length < 9) {
                handleSsnChange(`${ssnDigits}${key}`);
            }
            return;
        }

        e.preventDefault();
    };

    const handleMaskedSsnPaste = (e) => {
        if(showSsn) {
            return;
        }

        e.preventDefault();
        const pastedDigits = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 9);
        handleSsnChange(pastedDigits);
    };

    return (
        <div className="w-full max-w-5xl mx-auto px-4 pt-8 pb-4">
            <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-navy mb-4 whitespace-nowrap">
                    Let&apos;s find out how much you can invest
                </h1>
                <p className="text-gray-600">
                    The law limits how much you can invest in each year.
                </p>
            </div>

            <div className="flex flex-col lg:flex-row gap-10 lg:items-start items-stretch">
                {/* Left Column - Input Fields */}
                <div className="w-full lg:flex-1 max-w-xl space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-lg font-medium text-navy mb-2">
                                Date of Birth
                            </label>
                            <input
                                type="date"
                                placeholder="YYYY-MM-DD"
                                value={formData.birthdate || ""}
                                max={(() => {
                                    const d = new Date();
                                    d.setFullYear(d.getFullYear() - 18);
                                    return d.toISOString().split("T")[0];
                                })()}
                                onChange={(e) =>
                                    handleInputChange("birthdate", e.target.value)
                                }
                                className={`w-full p-2 border rounded-lg text-base ${
                                    errors.birthdate
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-navy focus:border-navy"
                                }`}
                            />
                            {errors.birthdate && (
                                <p className="text-red-500 text-sm mt-1">
                                    {errors.birthdate}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-lg font-medium text-navy mb-2">
                                Social Security Number
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="123-45-6789"
                                    value={ssnDisplayValue}
                                    onChange={(e) => {
                                        if(showSsn) {
                                            handleSsnChange(e.target.value);
                                        }
                                    }}
                                    onKeyDown={handleMaskedSsnKeyDown}
                                    onPaste={handleMaskedSsnPaste}
                                    inputMode="numeric"
                                    autoComplete="off"
                                    maxLength={11}
                                    className={`w-full p-2 pr-10 border rounded-lg text-base ${
                                        errors.ssn
                                            ? "border-red-500 focus:ring-red-500"
                                            : "border-gray-300 focus:ring-navy focus:border-navy"
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowSsn((prev) => !prev)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-navy"
                                    aria-label={showSsn ? "Hide Social Security Number" : "Show Social Security Number"}
                                >
                                    {showSsn ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                            {errors.ssn && (
                                <p className="text-red-500 text-sm mt-1">{errors.ssn}</p>
                            )}
                        </div>
                    </div>

                    {/* Net Worth */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <div className="flex items-center space-x-2">
                                <label className="block text-lg font-medium text-navy">
                                    What is your net worth?
                                </label>
                                <div className="relative inline-block">
                                    <button
                                        type="button"
                                        className="w-5 h-5 rounded-full bg-navy text-white text-xs font-bold flex items-center justify-center leading-none focus:outline-none"
                                        onMouseEnter={() => setShowNetWorthTooltip(true)}
                                        onMouseLeave={() => setShowNetWorthTooltip(false)}
                                        aria-label="Net worth information"
                                    >
                                        ?
                                    </button>
                                    {showNetWorthTooltip && (
                                        <div className="absolute left-0 top-full mt-2 p-4 bg-navy text-white text-sm rounded-lg shadow-lg w-80 z-20 whitespace-normal">
                                            <p className="text-xs leading-relaxed">
                                                Your net worth is the total value of everything you own (assets) minus everything you owe (liabilities). Assets include things like savings, investments, and property. Liabilities include debts like mortgages, loans, and credit card balances.
                                            </p>
                                            <p className="text-xs leading-relaxed mt-2 text-yellow-200">
                                                *Note: The value of your primary residence is excluded from this calculation.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className="text-lg font-medium">$</span>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.netWorth}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /\D/g,
                                            "",
                                        );
                                        handleInputChange("netWorth", value);
                                    }}
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy"
                                />
                            </div>
                        </div>

                        {/* Slider for net worth */}
                        <div className="mb-12 px-1">
                            <Slider
                                min={0}
                                max={1000000}
                                step={50000}
                                value={parseInt(formData.netWorth) || 0}
                                onChange={(value) =>
                                    handleInputChange(
                                        "netWorth",
                                        value.toString(),
                                    )
                                }
                                marks={{
                                    50000: "$50k",
                                    150000: "$100k",
                                    500000: "$500k",
                                    1000000: "$1M",
                                }}
                                trackStyle={{
                                    backgroundColor: "var(--navy)",
                                    height: 4,
                                }}
                                railStyle={{
                                    backgroundColor: "#e5e7eb",
                                    height: 4,
                                }}
                                handleStyle={{
                                    borderColor: "var(--navy)",
                                    backgroundColor: "var(--navy)",
                                    boxShadow:
                                        "0 2px 4px rgb(var(--navy-rgb) / 0.2)",
                                    width: 16,
                                    height: 16,
                                    marginTop: -6,
                                }}
                            />
                        </div>

                        {errors.netWorth && (
                            <p className="text-red-500 text-sm">
                                {errors.netWorth}
                            </p>
                        )}
                    </div>

                    {/* Annual Income */}
                    <div>
                        <div className="flex justify-between items-center mb-4">
                            <label className="block text-lg font-medium text-navy">
                                What is your annual income?
                            </label>
                            <div className="flex items-center space-x-2">
                                <span className="text-lg font-medium">$</span>
                                <input
                                    type="text"
                                    placeholder="0"
                                    value={formData.yearlyIncome}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /\D/g,
                                            "",
                                        );
                                        handleInputChange(
                                            "yearlyIncome",
                                            value,
                                        );
                                    }}
                                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy"
                                />
                            </div>
                        </div>

                        {/* Slider for annual income */}
                        <div className="mb-12 px-1">
                            <Slider
                                min={0}
                                max={300000}
                                step={5000}
                                value={parseInt(formData.yearlyIncome) || 0}
                                onChange={(value) =>
                                    handleInputChange(
                                        "yearlyIncome",
                                        value.toString(),
                                    )
                                }
                                marks={{
                                    50000: "$50k",
                                    100000: "$100k",
                                    200000: "$200k",
                                    300000: "$300k+",
                                }}
                                trackStyle={{
                                    backgroundColor: "var(--navy)",
                                    height: 4,
                                }}
                                railStyle={{
                                    backgroundColor: "#e5e7eb",
                                    height: 4,
                                }}
                                handleStyle={{
                                    borderColor: "var(--navy)",
                                    backgroundColor: "var(--navy)",
                                    boxShadow:
                                        "0 2px 4px rgb(var(--navy-rgb) / 0.2)",
                                    width: 16,
                                    height: 16,
                                    marginTop: -6,
                                }}
                            />
                        </div>

                        {errors.yearlyIncome && (
                            <p className="text-red-500 text-sm">
                                {errors.yearlyIncome}
                            </p>
                        )}
                    </div>

                    {/* Accredited Investor */}
                    <div>
                        <div className="flex items-center space-x-2 mb-4">
                            <label className="block text-lg font-medium text-navy">
                                Are you an{" "}
                                <span
                                    className="underline cursor-pointer relative inline-block"
                                    onMouseEnter={() =>
                                        setShowAccreditedTooltip(true)
                                    }
                                    onMouseLeave={() =>
                                        setShowAccreditedTooltip(false)
                                    }
                                >
                                    accredited investor
                                    {showAccreditedTooltip && (
                                        <div className="absolute left-0 top-full mt-2 p-4 bg-navy text-white text-sm rounded-lg shadow-lg w-96 z-20 whitespace-normal">
                                            <h4 className="font-semibold mb-2">
                                                Accredited Investor Definition
                                            </h4>
                                            <p className="mb-3 text-xs">
                                                An &quot;accredited
                                                investor&quot; is someone the
                                                SEC considers financially
                                                sophisticated enough to take on
                                                higher-risk, private investments
                                                without the usual protections.
                                                You qualify if you meet any one
                                                of these:
                                            </p>

                                            <div className="space-y-3 text-xs">
                                                <div>
                                                    <h5 className="font-semibold text-yellow-200 mb-1">
                                                        Individuals
                                                    </h5>
                                                    <ul className="space-y-1 ml-2">
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Income:
                                                            </strong>{" "}
                                                            $200,000+ each of
                                                            the last two years
                                                            (or $300,000+
                                                            combined with a
                                                            spouse) and
                                                            reasonable
                                                            expectation of the
                                                            same this year
                                                        </li>
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Net worth:
                                                            </strong>{" "}
                                                            Over $1,000,000
                                                            (alone or with
                                                            spouse), excluding
                                                            your primary
                                                            residence
                                                        </li>
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Professional
                                                                credentials:
                                                            </strong>{" "}
                                                            Hold certain FINRA
                                                            licenses (e.g.,
                                                            Series 7, 65, or 82)
                                                            and are in good
                                                            standing
                                                        </li>
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Private fund
                                                                insiders:
                                                            </strong>{" "}
                                                            &quot;Knowledgeable
                                                            employees&quot; of a
                                                            private fund, for
                                                            investments in that
                                                            fund
                                                        </li>
                                                    </ul>
                                                </div>

                                                <div>
                                                    <h5 className="font-semibold text-yellow-200 mb-1">
                                                        Entities
                                                    </h5>
                                                    <ul className="space-y-1 ml-2">
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Assets:
                                                            </strong>{" "}
                                                            Any entity (LLC,
                                                            trust, nonprofit,
                                                            etc.) with over
                                                            $5,000,000 in assets
                                                            or investments, not
                                                            formed just to buy a
                                                            specific security
                                                        </li>
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                All-accredited
                                                                owners:
                                                            </strong>{" "}
                                                            Any entity where all
                                                            equity owners are
                                                            accredited
                                                        </li>
                                                        <li>
                                                            •{" "}
                                                            <strong>
                                                                Professionals:
                                                            </strong>{" "}
                                                            Registered
                                                            investment advisers,
                                                            family offices with
                                                            at least $5,000,000
                                                            in assets under
                                                            management, certain
                                                            banks,
                                                            broker-dealers,
                                                            insurance companies,
                                                            etc.
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="mt-3 pt-2 border-t border-blue-300">
                                                <p className="text-xs text-blue-200">
                                                    <strong>
                                                        Quick context for Reg CF
                                                        (crowdfunding):
                                                    </strong>{" "}
                                                    Both accredited and
                                                    non-accredited investors may
                                                    participate. Investment
                                                    limits apply to
                                                    non-accredited investors;
                                                    accredited investors
                                                    aren&apos;t subject to those
                                                    Reg CF dollar caps.
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </span>
                                ?
                            </label>
                        </div>

                        <div className="flex space-x-4">
                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="radio"
                                    name="accredited"
                                    value="false"
                                    checked={
                                        formData.isAccreditedInvestor ===
                                        "false"
                                    }
                                    onChange={() =>
                                        handleInputChange(
                                            "isAccreditedInvestor",
                                            "false",
                                        )
                                    }
                                    className="w-4 h-4 text-navy focus:ring-navy"
                                />
                                <span className="text-gray-700">No</span>
                            </label>

                            <label className="flex items-center space-x-3 cursor-pointer">
                                <input
                                    type="radio"
                                    name="accredited"
                                    value="true"
                                    checked={
                                        formData.isAccreditedInvestor === "true"
                                    }
                                    onChange={() =>
                                        handleInputChange(
                                            "isAccreditedInvestor",
                                            "true",
                                        )
                                    }
                                    className="w-4 h-4 text-navy focus:ring-navy"
                                />
                                <span className="text-gray-700">Yes</span>
                            </label>
                        </div>

                        {errors.isAccreditedInvestor && (
                            <p className="text-red-500 text-sm mt-2">
                                {errors.isAccreditedInvestor}
                            </p>
                        )}
                    </div>

                    {/* Accredited Investor Type (conditional) */}
                    {formData.isAccreditedInvestor === "true" && (
                        <div>
                            <label className="block text-lg font-medium text-navy mb-4">
                                How do you qualify as an accredited investor?
                            </label>
                            <select
                                value={formData.accreditedInvestorType || ""}
                                onChange={(e) =>
                                    handleInputChange(
                                        "accreditedInvestorType",
                                        e.target.value,
                                    )
                                }
                                className={`w-full p-3 border rounded-lg text-lg ${
                                    errors.accreditedInvestorType
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300 focus:ring-navy focus:border-navy"
                                }`}
                            >
                                <option value="">
                                    Select qualification type
                                </option>
                                <option value="Pending">Pending</option>
                                <option value="Self_Accredited">
                                    Self Accredited
                                </option>
                                <option value="Verified_Accredited">
                                    Verified Accredited
                                </option>
                                <option value="Not_Accredited">
                                    Not Accredited
                                </option>
                            </select>

                            {errors.accreditedInvestorType && (
                                <p className="text-red-500 text-sm mt-2">
                                    {errors.accreditedInvestorType}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Prior 12-month Reg CF Investments (mandatory for all) */}
                    <div>
                        <label className="block text-lg font-medium text-navy mb-4">
                            Total amount you have already invested in any
                            Regulation Crowdfunding offerings in the last 12
                            months (all portals).
                        </label>
                        <div className="flex items-center space-x-2 mb-2">
                            <span className="text-lg font-medium">$</span>
                            <input
                                type="text"
                                placeholder="0"
                                value={formData.priorRegCfAmount || ""}
                                onChange={(e) => {
                                    let value = e.target.value;
                                    // Remove non-numeric characters except decimal point
                                    value = value.replace(/[^0-9.]/g, "");
                                    // Remove leading zeros but keep single zero
                                    value = value.replace(/^0+(?=\d)/, "");
                                    handleInputChange(
                                        "priorRegCfAmount",
                                        value,
                                    );
                                }}
                                className={`w-32 px-3 py-2 border rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy ${
                                    errors.priorRegCfAmount
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-300"
                                }`}
                            />
                        </div>
                        {errors.priorRegCfAmount && (
                            <p className="text-red-500 text-sm mt-2">
                                {errors.priorRegCfAmount}
                            </p>
                        )}
                    </div>
                </div>

                {/* Right Column - Investment Limit Display */}
                <div className="w-full lg:w-80 max-w-md lg:sticky lg:top-8">
                    {formData.netWorth && formData.yearlyIncome && (
                        <div className="bg-[#f0f6fa] border border-[#d1e2f0] p-6 rounded-xl sticky top-80 shadow-sm text-left">
                            <h3 className="font-semibold text-navy text-sm mb-2">
                                You are legally allowed to invest
                            </h3>
                            <div className="text-3xl font-bold text-[#23304a] mb-1">
                                {formData.isAccreditedInvestor === "true"
                                    ? "No Limit"
                                    : `${formatCurrency(annualLimit)} / yr`}
                            </div>
                            {formData.isAccreditedInvestor !== "true" && (
                                <div className="text-sm font-semibold text-[#6b7a8f] mb-3">
                                    Remaining This Period:{" "}
                                    <span className="text-[#23304a] font-bold">
                                        {formatCurrency(remainingLimit)}
                                    </span>
                                </div>
                            )}
                            {formData.isAccreditedInvestor !== "true" && (
                                <div className="mb-0">
                                    <span
                                        className="text-xs font-semibold text-navy underline decoration-navy/30 hover:decoration-navy underline-offset-2 cursor-pointer relative inline-block transition-colors"
                                        onMouseEnter={() =>
                                            setShowTooltip(true)
                                        }
                                        onMouseLeave={() =>
                                            setShowTooltip(false)
                                        }
                                    >
                                        Why?
                                        {showTooltip && (
                                            <div
                                                className="absolute left-full ml-3 p-5 bg-[#23304a] text-white text-sm rounded-xl shadow-xl w-80 z-50 whitespace-normal"
                                                style={{
                                                    top: "50%",
                                                    transform:
                                                        "translateY(-50%)",
                                                }}
                                            >
                                                <h4 className="font-semibold text-[#f0f6fa] mb-3 text-sm">
                                                    Federal law sets the
                                                    investing limits for
                                                    Regulation Crowdfunding
                                                </h4>
                                                <ul className="text-xs space-y-2 text-[#ced6e0]">
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>Everyone can invest at least $2,500—no matter your income or net worth</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>If either your income or net worth is under $124K, you can invest up to 5% of the greater number</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>If both your income and net worth are $124K or more, you can invest up to 10% of the greater number, with a maximum cap of $124,000</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>Investment limits are for every 12-month period</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>Every other investment in a Regulation Crowdfunding offering counts towards these federal limits</span>
                                                    </li>
                                                    <li className="flex gap-2">
                                                        <span>•</span>
                                                        <span>There is no limit for accredited investors</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* Investment Summary Line */}
                            <div className="mt-5 pt-4 border-t border-[#d1e2f0] text-xs text-[#4A5E78] leading-relaxed">
                                Based on your net worth of{" "}
                                <span className="font-semibold text-[#23304a]">
                                    {formatCurrency(formData.netWorth)}
                                </span>{" "}
                                and annual income of{" "}
                                <span className="font-semibold text-[#23304a]">
                                    {formatCurrency(formData.yearlyIncome)}
                                </span>
                                ,{" "}
                                {formData.isAccreditedInvestor === "true" ? (
                                    <span className="font-medium text-navy">you qualify as an accredited investor with no investment limits.</span>
                                ) : (
                                    <>
                                        you can invest up to{" "}
                                        <span className="font-semibold text-[#23304a]">
                                            {formatCurrency(annualLimit)}
                                        </span>{" "}
                                        per year. After accounting for prior Reg
                                        CF investments of{" "}
                                        <span className="font-semibold text-[#23304a]">
                                            {formatCurrency(
                                                parseFloat(
                                                    formData.priorRegCfAmount ||
                                                        0,
                                                ),
                                            )}
                                        </span>
                                        , you have{" "}
                                        <span className="font-semibold text-[#23304a]">
                                            {formatCurrency(remainingLimit)}
                                        </span>{" "}
                                        remaining this period.
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between mt-8">
                <button
                    onClick={prevStep}
                    className="px-8 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition-all"
                >
                    Back
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={`aqi-primary-action rounded-lg px-8 py-3 font-semibold shadow-lg transition-all ${
                        isSubmitting
                            ? "cursor-not-allowed bg-gray-400 text-white"
                            : "bg-navy text-white hover:bg-navy-2"
                    }`}
                >
                    {isSubmitting ? "Submitting..." : "Complete Account"}
                </button>
            </div>
        </div>
    );
}

InvestmentLimitsStep.propTypes = {
    formData: PropTypes.shape({
        netWorth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        yearlyIncome: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        birthdate: PropTypes.string,
        ssn: PropTypes.string,
        isAccreditedInvestor: PropTypes.bool,
        accreditedInvestorType: PropTypes.string,
        priorRegCfAmount: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    }).isRequired,
    updateFormData: PropTypes.func.isRequired,
    prevStep: PropTypes.func.isRequired,
    handleFinalSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
};

export default InvestmentLimitsStep;
